import React from 'react';
import { Box, Button, Checkbox, Collapse, Dialog, DialogContent, Divider, FormControlLabel, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { IPreviewShareLink } from '../../store/previewStore';
import PreviewLinkAdvancedSettings from './PreviewLinkAdvancedSettings';
import useShareLinkForm from './useShareLinkForm';

interface ShareLinkDialogProps {
  open: boolean;
  onClose: () => void;
  courseId: number;
  coursePageId?: number;
  mode?: 'create' | 'edit';
  link?: IPreviewShareLink | null;
  onSaved?: (link: IPreviewShareLink) => void;
}

const sectionPadding = { px: { xs: 0, md: 0 }, py: { xs: 0, md: 0 } };

function ShareLinkDialog({ open, onClose, courseId, coursePageId, mode = 'create', link = null, onSaved }: ShareLinkDialogProps) {
  const theme = useTheme();
  const {
    advancedOpen,
    allowComments,
    copyPassword,
    copyShareUrl,
    expirationEnabled,
    expirationValue,
    handleAdvancedToggle,
    handleExpirationEnabledChange,
    handleMaxViewsEnabledChange,
    handlePasswordEnabledChange,
    handleStartPageEnabledChange,
    isEditMode,
    isPasswordAvailable,
    isSaved,
    isSubmitting,
    maxViews,
    maxViewsEnabled,
    pageOptions,
    password,
    passwordEnabled,
    privateComments,
    previewName,
    saveLink,
    savedRequiresPassword,
    setAllowComments,
    setExpirationValue,
    setMaxViews,
    setPassword,
    setPrivateComments,
    setPreviewName,
    setStartPageId,
    shareUrl,
    startPageEnabled,
    startPageId,
  } = useShareLinkForm({ open, courseId, coursePageId, mode, link, onSaved });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          borderRadius: 1,
          backgroundColor: theme.palette.bgTwo.main,
          color: theme.palette.bgTwo.contrastText,
          overflow: 'hidden',
          boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
        },
        elevation: 0,
      }}
    >
      <DialogContent sx={{ position: 'relative', px: 6, py: 5 }}>
        <IconButton sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }} onClick={onClose} disabled={isSubmitting}>
          <CloseIcon />
        </IconButton>

        <Stack spacing={4}>
          <Typography variant='h4'>{isEditMode ? 'Edit Preview Link' : 'Preview Settings'}</Typography>

          <Stack spacing={4}>
            <Box sx={sectionPadding}>
              <Typography variant='h6' sx={{ mb: 1 }}>
                Link Name
              </Typography>
              <TextField
                fullWidth
                size='small'
                value={previewName}
                onChange={(event) => setPreviewName(event.target.value)}
                placeholder='Default (server-generated)'
              />
            </Box>

            <Box sx={sectionPadding}>
              <Typography variant='h6' sx={{ mb: 1 }}>
                Preview Link
              </Typography>
              <TextField
                fullWidth
                value={shareUrl}
                size='small'
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Tooltip title='Copy link'>
                        <IconButton onClick={() => void copyShareUrl()}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant='caption' sx={{ mt: 1, display: 'block', minHeight: '1.25rem' }}>
                {!isSaved ? 'This link becomes active after you click Save.' : ' '}
              </Typography>
            </Box>

            {isPasswordAvailable && (
              <Box sx={sectionPadding}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={passwordEnabled}
                      onChange={(event) => handlePasswordEnabledChange(event.target.checked)}
                    />
                  }
                  label='Password protected'
                />
                <Box sx={{ pl: 4, pt: 1, maxWidth: 360, display: passwordEnabled ? 'block' : 'none' }} aria-hidden={!passwordEnabled}>
                  <TextField
                    key={passwordEnabled ? 'password-enabled' : 'password-disabled'}
                    fullWidth
                    value={password ?? ''}
                    onChange={(event) => setPassword(event.target.value)}
                    type='text'
                    size='small'
                    label='Password'
                    placeholder={isSaved && savedRequiresPassword ? 'Enter a new password to replace the current one' : 'Enter password'}
                    helperText={isSaved && savedRequiresPassword && password.trim() === '' ? 'Leave blank to keep the current password.' : ' '}
                    autoComplete='off'
                    disabled={!passwordEnabled}
                    inputProps={{
                      'data-bwignore': 'true',
                      'data-lpignore': 'true',
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <Tooltip title='Copy password'>
                            <span>
                              <IconButton onClick={() => void copyPassword()} disabled={password.trim() === ''}>
                                <ContentCopyIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'end', width: '100%' }}>
              <Button
                variant='text'
                onClick={handleAdvancedToggle}
                sx={{
                  minWidth: 0,
                  px: 0,
                  textTransform: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: theme.palette.bgTwo.main,
                  },
                }}
              >
                Advanced
              </Button>
              <Divider sx={{ width: '100%' }} />
            </Box>

            <Collapse in={advancedOpen} timeout='auto' unmountOnExit>
              <Box sx={{ ...sectionPadding, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <PreviewLinkAdvancedSettings
                  title='Advanced Settings'
                  allowComments={allowComments}
                  privateComments={privateComments}
                  expirationEnabled={expirationEnabled}
                  expirationValue={expirationValue}
                  pageOptions={pageOptions}
                  startPageEnabled={startPageEnabled}
                  startPageId={startPageId}
                  onAllowCommentsChange={setAllowComments}
                  onPrivateCommentsChange={setPrivateComments}
                  onExpirationEnabledChange={handleExpirationEnabledChange}
                  onExpirationValueChange={setExpirationValue}
                  onStartPageEnabledChange={handleStartPageEnabledChange}
                  onStartPageIdChange={setStartPageId}
                />

                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={maxViewsEnabled}
                        onChange={(event) => handleMaxViewsEnabledChange(event.target.checked)}
                      />
                    }
                    label='Limit number of views'
                  />
                  <Box sx={{ pl: 4, pt: 1, maxWidth: 360 }}>
                    <TextField
                      type='number'
                      label='Max number of views'
                      placeholder='Max number of views (optional)'
                      size='small'
                      value={maxViews}
                      onChange={(event) => setMaxViews(event.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                      disabled={!maxViewsEnabled}
                      fullWidth
                    />
                  </Box>
                </Box>
              </Box>
            </Collapse>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end', pt: 1 }}>
              <Button variant='newSecondary' onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button variant='contained' onClick={saveLink} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default observer(ShareLinkDialog);
